import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  FlatList,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useAuthStore } from '../store/authStore';
import { useSavedVideosStore } from '../store/savedVideosStore';
import { useHistoryStore } from '../store/historyStore';
import { RootStackParamList } from '../types/navigation';
import { VideoPost } from '../types/video';
import * as blueskyService from '../services/bluesky';
import { convertPostToVideoPost } from '../utils/video';
import { VideoDurationBadge } from '../components/video/VideoDurationBadge';
import { AccountSwitcherModal } from '../components/common/AccountSwitcherModal';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const HorizontalVideoCard = ({ video, onPress }: { video: VideoPost; onPress: () => void }) => (
  <TouchableOpacity style={styles.horizontalCard} onPress={onPress}>
    <View style={styles.thumbnailContainer}>
      <Image source={{ uri: video.thumbnailUrl }} style={styles.thumbnail} />
      <VideoDurationBadge 
        initialDuration={video.duration} 
        videoUrl={video.videoUrl} 
        videoType={video.type} 
      />
    </View>
    <Text style={styles.videoTitle} numberOfLines={2}>{video.title}</Text>
    <Text style={styles.channelName} numberOfLines={1}>{video.channelName}</Text>
  </TouchableOpacity>
);

export const ProfileScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { profile, logout } = useAuthStore();
  const { savedVideos, loadSavedVideos } = useSavedVideosStore();
  const { history, loadHistory } = useHistoryStore();
  const [userVideos, setUserVideos] = useState<VideoPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAccountSwitcher, setShowAccountSwitcher] = useState(false);

  useEffect(() => {
    loadSavedVideos();
    loadHistory();
    if (profile) {
      loadUserVideos();
    }
  }, [profile]);

  const loadUserVideos = async () => {
    if (!profile) return;
    try {
      setIsLoading(true);
      const result = await blueskyService.getAuthorVideoPosts(profile.handle);
      const mapped = result.feed
        .map(convertPostToVideoPost)
        .filter((v): v is VideoPost => v !== null);
      setUserVideos(mapped);
    } catch (error) {
      console.error('Failed to load user videos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigation.reset({
      index: 0,
      routes: [{ name: 'Login' }],
    });
  };

  if (!profile) return null;

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView style={styles.container}>
        {/* ユーザーヘッダー */}
        <View style={styles.userHeader}>
          {profile.avatar ? (
            <Image source={{ uri: profile.avatar }} style={styles.mainAvatar} />
          ) : (
            <View style={[styles.mainAvatar, { backgroundColor: '#333' }]} />
          )}
          <View style={styles.userInfo}>
            <Text style={styles.displayName}>{profile.displayName || profile.handle}</Text>
            <View style={styles.handleRow}>
              <Text style={styles.handle}>@{profile.handle}</Text>
              <TouchableOpacity 
                onPress={() => navigation.navigate('ChannelDetail', { did: profile.did, handle: profile.handle })}
              >
                <Text style={styles.viewChannel}>チャンネルを表示 {'>'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* クイックボタン */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.quickButtons}>
          <TouchableOpacity 
            style={styles.quickButton}
            onPress={() => setShowAccountSwitcher(true)}
          >
            <Ionicons name="people-outline" size={20} color="#fff" />
            <Text style={styles.quickButtonText}>アカウントを切り替え</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.quickButton}
            onPress={() => navigation.navigate('AccountSettings')}
          >
            <Ionicons name="person-outline" size={20} color="#fff" />
            <Text style={styles.quickButtonText}>マイページ</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.quickButton} onPress={handleLogout}>
            <Ionicons name="log-out-outline" size={20} color="#ff4d4d" />
            <Text style={[styles.quickButtonText, { color: '#ff4d4d' }]}>ログアウト</Text>
          </TouchableOpacity>
        </ScrollView>

        {/* 履歴セクション */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>履歴</Text>
            <TouchableOpacity 
              style={styles.viewAllButton}
              onPress={() => {
                if (history.length === 0) return;
                navigation.navigate('VideoList', { 
                  videos: history,
                  title: '履歴'
                });
              }}
            >
              <Text style={styles.viewAllText}>すべて表示</Text>
            </TouchableOpacity>
          </View>
          <FlatList
            horizontal
            showsHorizontalScrollIndicator={false}
            data={history.slice(0, 10)}
            keyExtractor={(item) => 'history-' + item.id}
            renderItem={({ item }) => (
              <HorizontalVideoCard 
                video={item} 
                onPress={() => navigation.navigate('VideoPlayback', { videoId: item.id, video: item })}
              />
            )}
            ListEmptyComponent={
              <Text style={styles.emptyText}>再生した動画はありません</Text>
            }
            contentContainerStyle={styles.horizontalScrollContent}
          />
        </View>

        {/* プレイリストセクション (保存済み動画) */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>プレイリスト</Text>
            <TouchableOpacity 
              style={styles.viewAllButton}
              onPress={() => {
                if (savedVideos.length === 0) return;
                navigation.navigate('VideoList', { 
                  videos: savedVideos,
                  title: '保存済み'
                });
              }}
            >
              <Text style={styles.viewAllText}>すべて表示</Text>
            </TouchableOpacity>
          </View>
          <FlatList
            horizontal
            showsHorizontalScrollIndicator={false}
            data={savedVideos}
            keyExtractor={(item) => 'pl-' + item.id}
            renderItem={({ item }) => (
              <HorizontalVideoCard 
                video={item} 
                onPress={() => navigation.navigate('VideoPlayback', { videoId: item.id, video: item })}
              />
            )}
            ListEmptyComponent={
              <Text style={styles.emptyText}>保存した動画はありません</Text>
            }
            contentContainerStyle={styles.horizontalScrollContent}
          />
        </View>

        {/* 自分の動画セクション */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>自分の動画</Text>
            <TouchableOpacity 
              style={styles.viewAllButton}
              onPress={() => {
                if (userVideos.length === 0) return;
                navigation.navigate('VideoList', { 
                  videos: userVideos,
                  title: '自分の動画'
                });
              }}
            >
              <Text style={styles.viewAllText}>すべて表示</Text>
            </TouchableOpacity>
          </View>
          {isLoading ? (
            <ActivityIndicator size="small" color="#fff" style={{ marginVertical: 20 }} />
          ) : (
            <FlatList
              horizontal
              showsHorizontalScrollIndicator={false}
              data={userVideos}
              keyExtractor={(item) => 'user-' + item.id}
              renderItem={({ item }) => (
                <HorizontalVideoCard 
                  video={item} 
                  onPress={() => navigation.navigate('VideoPlayback', { videoId: item.id, video: item })}
                />
              )}
              ListEmptyComponent={
                <Text style={styles.emptyText}>投稿した動画はありません</Text>
              }
              contentContainerStyle={styles.horizontalScrollContent}
            />
          )}
        </View>

        {/* 下部メニュー項目 */}
        <View style={styles.menuItems}>
          <TouchableOpacity style={styles.menuItem}>
            <Ionicons name="download-outline" size={24} color="#fff" />
            <View style={styles.menuItemTextContainer}>
              <Text style={styles.menuItemText}>オフライン</Text>
              <Text style={styles.menuItemSubtext}>0 本の動画</Text>
            </View>
          </TouchableOpacity>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>

      <AccountSwitcherModal
        visible={showAccountSwitcher}
        onClose={() => setShowAccountSwitcher(false)}
        currentAccount={profile ? {
          did: profile.did,
          handle: profile.handle,
          displayName: profile.displayName,
          avatar: profile.avatar,
        } : undefined}
        accounts={profile ? [{
          did: profile.did,
          handle: profile.handle,
          displayName: profile.displayName,
          avatar: profile.avatar,
        }] : []}
        onSelectAccount={(account) => {
          // 現在は1アカウントのみなので何もしない
          console.log('Selected account:', account);
        }}
        onAddAccount={() => {
          handleLogout();
        }}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#000',
  },
  container: {
    flex: 1,
  },
  topActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  settingsIcon: {
    padding: 4,
  },
  userHeader: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingBottom: 20,
    alignItems: 'center',
  },
  mainAvatar: {
    width: 70,
    height: 70,
    borderRadius: 35,
    marginRight: 16,
  },
  userInfo: {
    flex: 1,
  },
  displayName: {
    color: '#fff',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  handleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  handle: {
    color: '#aaa',
    fontSize: 14,
    marginRight: 8,
  },
  viewChannel: {
    color: '#aaa',
    fontSize: 14,
  },
  quickButtons: {
    paddingHorizontal: 12,
    marginBottom: 24,
  },
  quickButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#222',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
  },
  quickButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 6,
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
  },
  viewAllButton: {
    borderWidth: 1,
    borderColor: '#333',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 15,
  },
  viewAllText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  horizontalScrollContent: {
    paddingLeft: 16,
    paddingRight: 8,
  },
  horizontalCard: {
    width: 160,
    marginRight: 12,
  },
  thumbnailContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    position: 'relative',
    marginBottom: 8,
  },
  thumbnail: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
  },
  durationContainer: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
  },
  durationText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  videoTitle: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '500',
    lineHeight: 18,
    marginBottom: 2,
  },
  channelName: {
    color: '#aaa',
    fontSize: 12,
  },
  emptyText: {
    color: '#555',
    fontSize: 14,
    marginLeft: 16,
    marginVertical: 10,
  },
  menuItems: {
    paddingHorizontal: 16,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
  },
  menuItemTextContainer: {
    marginLeft: 16,
  },
  menuItemText: {
    color: '#fff',
    fontSize: 16,
  },
  menuItemSubtext: {
    color: '#aaa',
    fontSize: 12,
    marginTop: 2,
  },
});
